import { css } from "@emotion/react";
import {
  PropsWithChildren,
  RefObject,
  createContext,
  useContext,
  useRef,
} from "react";
import Theme from "../../Theme/client";
import FieldProps from "../FieldProps";
import { FieldValidity } from "../utilities";
import HeadlessFieldsContext from "./HeadlessFieldsContext";
import {
  backgroundPerDisplay,
  borderColorPerDisplay,
  FieldDisplay,
} from "./display";
import FieldLabel from "./FieldLabel";
import T from "@hpo/client/components/text/Text";

type FieldWrapperProps = PropsWithChildren<FieldProps> & {
  validity?: FieldValidity;
  finalized: boolean;
};

export default function FieldWrapper(props: FieldWrapperProps) {
  const { id, label, help, children, validity, readonly, disabled, finalized } =
    props;

  const headlessContext = useContext(HeadlessFieldsContext);
  const isHeadless =
    props.headless === undefined ? headlessContext : props.headless;

  const display: FieldDisplay = readonly
    ? "readonly"
    : disabled
      ? "disabled"
      : "normal";

  const ref = useRef<HTMLDivElement>(null);

  const containerStyle = css({
    display: "flex",
    flexDirection: "column",
  });

  const inputStyle = css({
    borderWidth: isHeadless ? 0 : 1,
    borderColor: borderColorPerDisplay[display],
    borderStyle: "solid",
    background: backgroundPerDisplay[display],
    borderRadius: isHeadless ? 6 : 6,
  });

  const showValidity = finalized !== false;

  const showRequiredSymbol = props.required && !readonly && !disabled;

  if (isHeadless) {
    return <div css={inputStyle}>{children}</div>;
  }

  return (
    <FieldWrapperElementContext.Provider value={ref}>
      <div css={containerStyle} ref={ref}>
        <FieldLabel
          htmlFor={id}
          display={display}
          loading={props.loading}
          text={label}
          showRequiredSymbol={showRequiredSymbol}
        />
        <div css={inputStyle}>{children}</div>
        {help ? (
          <T style="minor" color={Theme.text.fade} id={`${id}-help`}>
            {help}
          </T>
        ) : null}
        {validity && !validity.valid && showValidity ? (
          <T style="minor" id={`${id}-validity`} color={Theme.error}>
            {validity.message}
          </T>
        ) : null}
      </div>
    </FieldWrapperElementContext.Provider>
  );
}

export const FieldWrapperElementContext = createContext<
  RefObject<HTMLDivElement>
>({
  current: null,
});
